import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { User, Building, Users, CreditCard } from "lucide-react";
import { RitualIcon } from "@/components/ui/RitualIcon";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { MemberManagement } from "@/components/members/MemberManagement";
import { usePlanLimits } from "@/hooks/use-plan-limits";
import { UsageCard } from "@/components/billing/UsageCard";
import { UpgradePrompt } from "@/components/billing/UpgradePrompt";
import { PLAN_LIMITS } from "@/lib/plan-limits";

interface OutletContext {
  user: any;
  organizationId: string;
}

const Settings = () => {
  const { user, organizationId } = useOutletContext<OutletContext>();
  const [profile, setProfile] = useState({ name: "", time_zone: "UTC" });
  const [organization, setOrganization] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const { planTier, limits, usage } = usePlanLimits(organizationId);

  useEffect(() => {
    fetchData();
  }, [user, organizationId]);

  const fetchData = async () => {
    // Fetch profile
    const { data: profileData } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", user.id)
      .single();

    if (profileData) {
      setProfile({
        name: profileData.name || "",
        time_zone: profileData.time_zone || "UTC",
      });
    }

    // Fetch organization
    const { data: orgData } = await supabase
      .from("organizations")
      .select("*")
      .eq("id", organizationId)
      .single();

    if (orgData) {
      setOrganization(orgData);
    }
  };

  const handleProfileUpdate = async () => {
    setLoading(true);
    try {
      const { error } = await supabase
        .from("profiles")
        .update({
          name: profile.name,
          time_zone: profile.time_zone,
        })
        .eq("id", user.id);

      if (error) throw error;

      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully.",
      });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container max-w-4xl py-6 md:py-8 space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold mb-1 md:mb-2">Settings</h1>
        <p className="text-sm md:text-base text-muted-foreground">
          Manage your account and organization settings
        </p>
      </div>

      <Tabs defaultValue="profile" className="space-y-6">
        <TabsList className="w-full sm:w-auto flex flex-wrap gap-1">
          <TabsTrigger value="profile" className="flex-1 sm:flex-initial text-xs sm:text-sm">
            <User className="mr-1 sm:mr-2 h-4 w-4" />
            <span className="hidden xs:inline">Profile</span>
          </TabsTrigger>
          <TabsTrigger value="organization" className="flex-1 sm:flex-initial text-xs sm:text-sm">
            <Building className="mr-1 sm:mr-2 h-4 w-4" />
            <span className="hidden xs:inline">Organization</span>
          </TabsTrigger>
          <TabsTrigger value="members" className="flex-1 sm:flex-initial text-xs sm:text-sm">
            <Users className="mr-1 sm:mr-2 h-4 w-4" />
            <span className="hidden xs:inline">Members</span>
          </TabsTrigger>
          <TabsTrigger value="billing" className="flex-1 sm:flex-initial text-xs sm:text-sm">
            <CreditCard className="mr-1 sm:mr-2 h-4 w-4" />
            <span className="hidden xs:inline">Billing</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile">
          <Card>
            <CardHeader>
              <CardTitle>Profile Settings</CardTitle>
              <CardDescription>
                Manage your personal account settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" value={user.email} disabled />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="timezone">Time Zone</Label>
                <Input
                  id="timezone"
                  value={profile.time_zone}
                  onChange={(e) => setProfile({ ...profile, time_zone: e.target.value })}
                />
              </div>
              <Button onClick={handleProfileUpdate} disabled={loading} className="w-full sm:w-auto">
                Save Changes
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="organization">
          <Card>
            <CardHeader>
              <CardTitle>Organization Settings</CardTitle>
              <CardDescription>
                Manage your organization details
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {organization && (
                <>
                  <div className="space-y-2">
                    <Label>Organization Name</Label>
                    <Input value={organization.name} disabled />
                  </div>
                  <div className="space-y-2">
                    <Label>Country</Label>
                    <Input value={organization.country} disabled />
                  </div>
                  <div className="space-y-2">
                    <Label>Plan</Label>
                    <Input value={organization.plan_tier.toUpperCase()} disabled />
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="members">
          <MemberManagement organizationId={organizationId} currentUserId={user.id} />
        </TabsContent>

        <TabsContent value="billing" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    Current Plan
                    <Badge variant="secondary" className="bg-primary/10 text-primary">
                      <RitualIcon size="xs" className="mr-1" />
                      {planTier.charAt(0).toUpperCase() + planTier.slice(1)}
                    </Badge>
                  </CardTitle>
                  <CardDescription>
                    Manage your subscription and billing
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {organization && (
                <>
                  <div className="grid sm:grid-cols-3 gap-4">
                    <div className="p-4 rounded-lg bg-muted/30 border">
                      <p className="text-sm text-muted-foreground">Monthly Price</p>
                      <p className="text-2xl font-bold mt-1">
                        ₹{PLAN_LIMITS[planTier].priceINR}
                        <span className="text-sm font-normal text-muted-foreground">/mo</span>
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/30 border">
                      <p className="text-sm text-muted-foreground">Team Seats</p>
                      <p className="text-2xl font-bold mt-1">
                        {usage?.seatsUsed || 0}
                        <span className="text-sm font-normal text-muted-foreground">/{limits.maxSeats}</span>
                      </p>
                    </div>
                    <div className="p-4 rounded-lg bg-muted/30 border">
                      <p className="text-sm text-muted-foreground">AI Calls Today</p>
                      <p className="text-2xl font-bold mt-1">
                        {usage?.aiCallsToday || 0}
                        <span className="text-sm font-normal text-muted-foreground">/{limits.rateLimits.aiCallsPerDay}</span>
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 border rounded-lg">
                    <div>
                      <h3 className="font-semibold">
                        {organization.plan_tier.charAt(0).toUpperCase() + organization.plan_tier.slice(1)} Plan
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {organization.plan_seat_limit} seats • Unlimited SOPs & runs
                      </p>
                    </div>
                    <Button variant="outline" className="w-full sm:w-auto" asChild>
                      <a href="/pricing">Change Plan</a>
                    </Button>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
          
          <UsageCard usage={usage} limits={limits} />
          
          {planTier !== 'opspro' && (
            <UpgradePrompt currentPlan={planTier} />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default Settings;
